// Cell formatters for the data-table controller. A cell is chosen with
//   data-field="<dotted.path>"  data-format="<name>[:<arg>]"  [data-fallback="<other field>"]
// and an element may carry data-href="/path/?order={order_code}" (href filled from the row; no href when a field is empty)
// and data-visible-if="<field>" (hidden unless the field is truthy). DOM only through textContent/attributes.

import { formatDate } from "./api.js";
import { resolvePill } from "./data_table_pills.js";

const DASH = "—";
const _moneyFmt = new Intl.NumberFormat("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: "always" });

export function getPath(item, path) {
  if (!path) return undefined;
  return path.split(".").reduce((acc, key) => (acc === null || acc === undefined ? undefined : acc[key]), item);
}

const isEmpty = (v) => v === null || v === undefined || v === "";

// "1234.5" -> "1 234,50 zł"
export function formatMoney(value) {
  const num = Number(value);
  if (isEmpty(value) || !Number.isFinite(num)) return DASH;
  return `${_moneyFmt.format(num)} zł`;
}

export function formatDay(iso) {
  if (!iso) return DASH;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return DASH;
  return d.toLocaleDateString("pl-PL", { day: "2-digit", month: "2-digit", year: "numeric" });
}

// Polish plural: 1 -> one, 2-4 (not 12-14) -> few, otherwise many. forms = "lead,leady,leadów".
export function plural(n, forms) {
  const [one, few, many] = forms.split(",");
  const num = Number(n);
  if (!Number.isFinite(num)) return DASH;
  const mod10 = num % 10;
  const mod100 = num % 100;
  const word = num === 1 ? one : (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14) ? few : many);
  return `${num} ${word}`;
}

// Each formatter: (value, item, arg) -> string | { text, cls?, pill? }
export const FORMATTERS = {
  text:     (v) => (isEmpty(v) ? DASH : String(v)),
  money:    (v) => formatMoney(v),
  date:     (v) => formatDay(v),
  datetime: (v) => formatDate(v),
  plural:   (v, _item, arg) => (isEmpty(v) ? DASH : plural(v, arg || "")),
  prefix:   (v, _item, arg) => (isEmpty(v) ? DASH : `${arg || ""}${v}`),
  list:     (v) => (Array.isArray(v) && v.length ? v.join(", ") : DASH),
  // milliseconds -> "1,3 s" (pl-PL decimal comma)
  duration: (v) => {
    const ms = Number(v);
    if (isEmpty(v) || !Number.isFinite(ms)) return DASH;
    return `${(ms / 1000).toLocaleString("pl-PL", { maximumFractionDigits: 1 })} s`;
  },
  // "attempt" + arg "max_attempts" -> "2/3"
  fraction: (v, item, arg) => {
    const other = getPath(item, arg);
    return isEmpty(v) || isEmpty(other) ? DASH : `${v}/${other}`;
  },
  label: (v, item, arg) => {
    const hit = resolvePill(arg, v, item);
    return hit ? hit.text : DASH;
  },
  pill: (v, item, arg) => {
    const hit = resolvePill(arg, v, item);
    return hit ? { text: hit.text, cls: hit.cls, pill: true } : DASH;
  },
};

function splitFormat(spec) {
  const at = spec.indexOf(":");
  return at === -1 ? [spec, ""] : [spec.slice(0, at), spec.slice(at + 1)];
}

// Fill one [data-field] element from a row item.
export function fillCell(el, item) {
  const [name, arg] = splitFormat(el.dataset.format || "text");
  const format = FORMATTERS[name] || FORMATTERS.text;
  let value = getPath(item, el.dataset.field);
  if (isEmpty(value) && el.dataset.fallback) value = getPath(item, el.dataset.fallback);
  const out = format(value, item, arg);
  if (typeof out === "string") {
    el.textContent = out;
    return;
  }
  el.textContent = out.text;
  if (out.pill) el.className = `ls-pill ${out.cls || ""}`.trim();
}

// "/management/platnosci/?order={order_code}" -> href, or null when a placeholder field is empty.
export function hrefFromPattern(pattern, item) {
  let missing = false;
  const href = pattern.replace(/\{([\w.]+)\}/g, (_m, path) => {
    const v = getPath(item, path);
    if (isEmpty(v)) missing = true;
    return encodeURIComponent(String(v ?? ""));
  });
  return missing ? null : href;
}

// Fill a whole cloned row: values, hrefs, conditional visibility.
export function fillRow(row, item) {
  for (const el of row.querySelectorAll("[data-field]")) fillCell(el, item);
  for (const el of row.querySelectorAll("[data-href]")) {
    const href = hrefFromPattern(el.dataset.href, item);
    if (href === null) el.removeAttribute("href");
    else el.setAttribute("href", href);
  }
  for (const el of row.querySelectorAll("[data-visible-if]")) {
    el.hidden = !getPath(item, el.dataset.visibleIf);
  }
}
